/**
 * Phase 2b employer application wizard helpers.
 *
 * Shared save-status machine + debounced PATCH + URL-hash sync.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

import { API } from "./api";

const http = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
    timeout: 20000,
});

export async function startApplication() {
    const res = await http.post("/team/apply/start");
    return res.data;
}

export async function getApplication() {
    const res = await http.get("/team/apply");
    return res.data;
}

export async function patchApplicationStep(step, payload) {
    const res = await http.patch(`/team/apply/step/${step}`, payload);
    return res.data;
}

export async function advanceApplication() {
    const res = await http.post("/team/apply/advance");
    return res.data;
}

export async function backApplication() {
    const res = await http.post("/team/apply/back");
    return res.data;
}

export async function abandonApplication(reason) {
    const res = await http.post("/team/apply/abandon", { reason });
    return res.data;
}

export async function getRiders() {
    const res = await http.get("/team/apply/riders");
    return res.data;
}

export async function submitApplication(attested, attestationText) {
    const res = await http.post("/team/apply/submit", {
        attested,
        attestation_text: attestationText,
    });
    return res.data;
}

/**
 * Debounced autosave hook.
 *
 * Accepts a step number + a "live value" object that changes as the user
 * types. Fires PATCH /team/apply/step/:n after `delayMs` of stillness.
 * Returns a save-status string ('idle' | 'saving' | 'saved' | 'error') and
 * the last-save timestamp.
 *
 * The hook intentionally doesn't own application state — it only watches
 * the provided snapshot and POSTs diffs. The parent owns merging the
 * server response back into state.
 */
export function useAutosave({
    step,
    payload,
    enabled,
    delayMs = 750,
    onSaved,
    onError,
}) {
    const [status, setStatus] = useState("idle");
    const [savedAt, setSavedAt] = useState(null);
    const timer = useRef(null);
    const lastSent = useRef(null);

    useEffect(() => {
        if (!enabled) return;
        const snapshot = JSON.stringify(payload);
        if (snapshot === lastSent.current) return;

        if (timer.current) clearTimeout(timer.current);
        setStatus("saving");
        timer.current = setTimeout(async () => {
            try {
                const res = await patchApplicationStep(step, payload);
                lastSent.current = snapshot;
                setStatus("saved");
                setSavedAt(new Date());
                onSaved?.(res);
            } catch (e) {
                setStatus("error");
                onError?.(e);
            }
        }, delayMs);
        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(payload), step, enabled]);

    return { status, savedAt };
}
